import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  FlagIcon,
  FolderOpenIcon,
  PlusIcon,
  Trash2Icon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";

export default function MyTournaments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [scores, setScores] = useState({}); // tournamentId → score data
  const [expandedIds, setExpandedIds] = useState([]);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 640
  );

  useEffect(() => {
    if (!user) return;

    const fetchTournaments = async () => {
      setLoading(true);

      const { data, error } = await supabase
        .from("tournaments")
        .select(`
    *,
    tournament_players (
      *,
      players (
        id,
        name
      )
    ),
    rounds(*),
    mini_games(*)
  `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (!error) {
        setTournaments(data);
        fetchAllScores(data.map((t) => t.id));
      } else {
        console.error("Failed to fetch tournaments:", error);
      }

      setLoading(false);
    };

    const fetchAllScores = async (tournamentIds) => {
      const { data, error } = await supabase
        .from("scores")
        .select("round_id, player_id, score");

      if (error) {
        console.error("Could not fetch scores", error);
        return;
      }

      const scoreMap = {};

      tournamentIds.forEach((tid) => {
        scoreMap[tid] = [];
      });

      // get rounds per tournament
      const { data: allRounds } = await supabase
        .from("rounds")
        .select("id, tournament_id");

      const roundToTournament = {};
      allRounds?.forEach((r) => {
        roundToTournament[r.id] = r.tournament_id;
      });

      data?.forEach((s) => {
        const tournamentId = roundToTournament[s.round_id];
        if (tournamentId && scoreMap[tournamentId]) {
          scoreMap[tournamentId].push(s);
        }
      });

      // Aggregate scores per player per tournament
      const aggregated = {};
      for (const [tid, scores] of Object.entries(scoreMap)) {
        const playerTotals = {};
        scores.forEach((s) => {
          if (!playerTotals[s.player_id]) playerTotals[s.player_id] = 0;
          playerTotals[s.player_id] += s.score;
        });

        // sort by score ascending
        const sorted = Object.entries(playerTotals)
          .sort((a, b) => a[1] - b[1])
          .map(([player_id, total]) => ({ player_id, total }));

        aggregated[tid] = sorted;
      }

      setScores(aggregated);
    };

    fetchTournaments();
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDelete = async (id) => {
    const { error } = await supabase.from("tournaments").delete().eq("id", id);

    if (error) {
      console.error("Failed to delete tournament:", error);
      alert("Kunde inte radera turneringen.");
    } else {
      setTournaments((prev) => prev.filter((t) => t.id !== id));
      setDeleteCandidate(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto w-full max-w-5xl px-4 py-7 sm:px-6 sm:py-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {/* Page header */}
          <header className="relative overflow-hidden rounded-[26px] bg-[#183d2d] px-5 py-6 text-white shadow-[0_18px_50px_rgba(24,61,45,0.16)] sm:px-7 sm:py-7">
            <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-[#b08b45]/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />

            <div className="relative flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/55">
                  Turneringsarkiv
                </p>

                <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">
                  Mina turneringar
                </h1>

                <p className="mt-2 max-w-xl text-sm leading-6 text-white/65">
                  Öppna tidigare tävlingar, följ ställningen eller skapa nästa
                  turnering.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur-sm">
                  {tournaments.length}{" "}
                  {tournaments.length === 1 ? "turnering" : "turneringar"}
                </span>

                <button
                  type="button"
                  onClick={() => navigate("/ny")}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#b08b45] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#9f7b3c]"
                >
                  <PlusIcon className="h-4 w-4" />
                  Skapa ny
                </button>
              </div>
            </div>
          </header>

          {/* Tournament list */}
          <section className="mt-5">
            {loading ? (
              <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-sm">
                <div className="flex items-center gap-3 px-5 py-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                    <FolderOpenIcon className="h-5 w-5" />
                  </span>

                  <div>
                    <p className="text-sm font-semibold">
                      Laddar turneringar
                    </p>
                    <p className="mt-0.5 text-xs text-[#68716c]">
                      Hämtar ditt turneringsarkiv…
                    </p>
                  </div>
                </div>
              </div>
            ) : tournaments.length === 0 ? (
              <div className="rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-6 py-14 text-center shadow-sm">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
                  <TrophyIcon className="h-6 w-6" />
                </span>

                <h2 className="mt-4 text-xl font-semibold text-[#183d2d]">
                  Inga turneringar ännu
                </h2>

                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#68716c]">
                  Skapa din första turnering och samla ronder, spelare och
                  resultat på ett ställe.
                </p>

                <button
                  type="button"
                  onClick={() => navigate("/ny")}
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#183d2d] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#214d39]"
                >
                  <PlusIcon className="h-4 w-4" />
                  Skapa turnering
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {tournaments.map((tournament, index) => {
                  const leaderboard = scores[tournament.id] || [];
                  const isExpanded =
                    isDesktop || expandedIds.includes(tournament.id);

                  const toggleExpanded = () => {
                    if (isDesktop) return;

                    setExpandedIds((prev) =>
                      prev.includes(tournament.id)
                        ? prev.filter((id) => id !== tournament.id)
                        : [...prev, tournament.id]
                    );
                  };

                  return (
                    <motion.article
                      key={tournament.id}
                      className="min-w-0 overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_8px_24px_rgba(31,45,38,0.07)]"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.32,
                        delay: Math.min(index * 0.04, 0.24),
                      }}
                    >
                      <button
                        type="button"
                        onClick={toggleExpanded}
                        className="flex w-full items-start gap-3 px-4 py-4 text-left sm:cursor-default sm:px-5"
                        aria-expanded={isExpanded}
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e5ede7] text-[#183d2d]">
                          <FlagIcon className="h-5 w-5" />
                        </span>

                        <div className="min-w-0 flex-1">
                          <h2 className="break-words text-base font-semibold leading-snug text-[#183d2d] sm:text-lg">
                            {tournament.name}
                          </h2>

                          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-[#7b817d]">
                            <CalendarDaysIcon className="h-3.5 w-3.5" />
                            {new Date(
                              tournament.created_at
                            ).toLocaleDateString("sv-SE")}
                          </div>
                        </div>

                        <ChevronDownIcon
                          className={`mt-1 h-5 w-5 shrink-0 text-[#8a8f8b] transition-transform sm:hidden ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {/* Always-visible summary */}
                      <div className="grid grid-cols-3 border-y border-[#e4ded1] bg-[#f7f4ec]">
                        <div className="px-2 py-3 text-center">
                          <UsersIcon className="mx-auto h-4 w-4 text-[#9a7735]" />
                          <p className="mt-1 text-sm font-semibold tabular-nums text-[#183d2d]">
                            {tournament.tournament_players?.length || 0}
                          </p>
                          <p className="text-[8px] font-semibold uppercase tracking-[0.12em] text-[#7b817d]">
                            Spelare
                          </p>
                        </div>

                        <div className="border-x border-[#e4ded1] px-2 py-3 text-center">
                          <FlagIcon className="mx-auto h-4 w-4 text-[#9a7735]" />
                          <p className="mt-1 text-sm font-semibold tabular-nums text-[#183d2d]">
                            {tournament.rounds?.length || 0}
                          </p>
                          <p className="text-[8px] font-semibold uppercase tracking-[0.12em] text-[#7b817d]">
                            Ronder
                          </p>
                        </div>

                        <div className="px-2 py-3 text-center">
                          <TrophyIcon className="mx-auto h-4 w-4 text-[#9a7735]" />
                          <p className="mt-1 text-sm font-semibold tabular-nums text-[#183d2d]">
                            {tournament.mini_games?.length || 0}
                          </p>
                          <p className="text-[8px] font-semibold uppercase tracking-[0.12em] text-[#7b817d]">
                            Sidotävlingar
                          </p>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="px-4 py-4 sm:px-5">
                          {tournament.team_mode && (
                            <div className="mb-3 inline-flex items-center rounded-full bg-[#e5ede7] px-2.5 py-1 text-[10px] font-semibold text-[#287148]">
                              Två-lagsläge aktivt
                            </div>
                          )}

                          <div className="rounded-xl border border-[#e3ded2] bg-[#f7f4ec] px-3 py-3">
                            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#7b817d]">
                              Aktuell topp 3
                            </p>

                            {leaderboard.length > 0 ? (
                              <div className="mt-2 space-y-1.5">
                                {leaderboard.slice(0, 3).map((entry, i) => {
                                  const player =
                                    tournament.tournament_players?.find(
                                      (tp) =>
                                        tp.player_id === entry.player_id
                                    );

                                  return (
                                    <div
                                      key={entry.player_id}
                                      className="flex items-center justify-between gap-3 rounded-lg bg-[#fffdf8] px-3 py-2"
                                    >
                                      <div className="flex min-w-0 items-center gap-2">
                                        <span
                                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                                            i === 0
                                              ? "bg-[#183d2d] text-white"
                                              : "border border-[#d8d2c4] text-[#68716c]"
                                          }`}
                                        >
                                          {i + 1}
                                        </span>

                                        <span className="truncate text-xs font-medium text-[#1f2d26]">
                                          {player?.players?.name ||
                                            "Okänd spelare"}
                                        </span>
                                      </div>

                                      <span className="shrink-0 text-xs font-semibold tabular-nums text-[#183d2d]">
                                        {entry.total} slag
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="mt-2 text-xs text-[#8a8f8b]">
                                Inga resultat registrerade ännu.
                              </p>
                            )}
                          </div>

                          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(`/turnering/${tournament.id}`)
                              }
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#214d39]"
                            >
                              <ExternalLinkIcon className="h-4 w-4" />
                              Öppna turnering
                            </button>

                            <button
                              type="button"
                              onClick={() => setDeleteCandidate(tournament)}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-4 py-2.5 text-sm font-semibold text-[#a0443e] transition hover:bg-[#f7ebe8]"
                            >
                              <Trash2Icon className="h-4 w-4" />
                              Radera
                            </button>
                          </div>
                        </div>
                      )}
                    </motion.article>
                  );
                })}
              </div>
            )}
          </section>
        </motion.div>
      </main>

      {/* Delete confirmation */}
      {deleteCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#17231d]/60 px-4 backdrop-blur-sm">
          <motion.div
            className="w-full max-w-sm overflow-hidden rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] shadow-2xl"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-tournament-title"
          >
            <div className="border-b border-[#e4ded1] px-5 py-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f3e8e5] text-[#a0443e]">
                <Trash2Icon className="h-5 w-5" />
              </span>

              <h3
                id="delete-tournament-title"
                className="mt-3 text-lg font-semibold text-[#1f2d26]"
              >
                Radera turnering
              </h3>

              <p className="mt-2 text-sm leading-6 text-[#68716c]">
                Är du säker på att du vill radera{" "}
                <strong className="font-semibold text-[#1f2d26]">
                  {deleteCandidate.name}
                </strong>
                ? Detta går inte att ångra.
              </p>
            </div>

            <div className="flex justify-end gap-2 bg-[#f7f4ec] px-5 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                className="rounded-xl border border-[#d8d2c4] bg-[#fffdf8] px-4 py-2 text-sm font-semibold text-[#68716c] transition hover:bg-white"
              >
                Avbryt
              </button>

              <button
                type="button"
                onClick={() => handleDelete(deleteCandidate.id)}
                className="rounded-xl bg-[#a0443e] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#8e3934]"
              >
                Radera
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
